'use client'

import React from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { TextEffect } from '@/components/ui/text-effect'
import { AnimatedGroup } from '@/components/ui/animated-group'
import { HeroHeader } from '@/components/header'
import Footer from '@/components/footer'
import { 
  Check, 
  X, 
  ArrowRight, 
  Star,
  Zap,
  Users,
  Building,
  Crown,
  MessageSquare
} from 'lucide-react'

// Using design.json color palette and neubrutalism principles
const plans = [
  {
    name: "Starter",
    description: "Perfect for small events and getting started",
    price: "Free",
    period: "forever",
    icon: Users,
    popular: false,
    bgColor: "bg-[#ffaf68]", // Orange from design.json
    borderColor: "border-black",
    rotateClass: "rotate-2",
    features: [
      "Up to 50 event attendees",
      "Basic bingo games",
      "QR code event joining",
      "Basic analytics",
      "Email support",
      "Mobile app access"
    ],
    limitations: [
      "No voice commands",
      "No AI matching",
      "Limited customization",
      "Basic templates only"
    ],
    cta: "Get Started Free",
    href: "/signup"
  },
  {
    name: "Professional",
    description: "Advanced features for growing organizations",
    price: "$29",
    period: "per month",
    icon: Building,
    popular: true,
    bgColor: "bg-[#70b5ff]", // Blue from design.json
    borderColor: "border-black",
    rotateClass: "-rotate-1",
    features: [
      "Up to 500 event attendees",
      "Voice-powered interactions",
      "AI-powered matching",
      "Advanced analytics",
      "Custom branding",
      "Priority support",
      "API access",
      "Custom game templates",
      "Real-time insights",
      "Export capabilities"
    ],
    limitations: [],
    cta: "Start Free Trial",
    href: "/signup?plan=professional"
  },
  {
    name: "Enterprise",
    description: "Custom solutions for large organizations",
    price: "Custom",
    period: "pricing",
    icon: Crown,
    popular: false,
    bgColor: "bg-[#ba81ee]", // Purple from design.json
    borderColor: "border-black",
    rotateClass: "rotate-3",
    features: [
      "Unlimited attendees",
      "White-label solution",
      "Custom integrations",
      "Dedicated support",
      "SLA guarantees",
      "Advanced security",
      "Custom development",
      "Training & onboarding",
      "Multi-tenant architecture",
      "Custom analytics"
    ],
    limitations: [],
    cta: "Contact Sales",
    href: "/contact"
  }
]

const transitionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
}

export default function PricingPage() {
  return (
    <div className="w-full" style={{ backgroundColor: '#d9f3fc' }}> {/* Background from design.json */}
      <HeroHeader />
      <main className="relative overflow-hidden">
        {/* Scattered decorative elements following design.json */}
        <div className="absolute top-20 left-10 w-8 h-8 bg-[#f4889a] border-4 border-black transform rotate-45 shadow-xl"></div>
        <div className="absolute top-40 right-20 w-6 h-6 bg-[#79d45e] border-4 border-black rounded-full shadow-xl"></div>
        <div className="absolute bottom-32 left-1/4 w-5 h-12 bg-[#4928fd] border-4 border-black transform -rotate-12 shadow-xl"></div>
        <div className="absolute bottom-48 right-1/3 w-10 h-6 bg-[#ffaf68] border-4 border-black transform rotate-12 shadow-xl"></div>

        {/* Floating sticky notes */}
        <div className="absolute top-32 right-4 bg-[#f6e683] border-4 border-black p-3 transform rotate-12 shadow-xl max-w-32 hidden md:block">
          <p className="text-xs font-black text-black">"Start free, scale smart!"</p>
        </div>

        {/* Hero Section */}
        <section className="relative px-6 py-16 md:py-32">
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#4928fd] border-4 border-black p-3 inline-block transform -rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <Badge variant="outline" className="border-white text-white bg-transparent">
                  <Star className="mr-2 h-4 w-4" />
                  SIMPLE, TRANSPARENT PRICING
                </Badge>
              </div>
            </AnimatedGroup>

            <div className="bg-[#ffaf68] border-4 border-black p-8 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-black leading-tight">
                CHOOSE YOUR<br />PERFECT PLAN
              </h1>
            </div>

            <div className="bg-white border-4 border-black p-6 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 max-w-2xl">
              <p className="text-lg font-semibold text-black">
                Start free and scale as you grow. All plans include our core networking features with no hidden fees.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="relative px-6 py-16">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-3">
              {plans.map((plan, index) => (
                <AnimatedGroup
                  key={plan.name}
                  variants={{
                    item: {
                      ...transitionVariants,
                      visible: {
                        ...transitionVariants.visible,
                        transition: {
                          ...transitionVariants.visible.transition,
                          delay: index * 0.1
                        }
                      }
                    }
                  }}>
                  <div className={`relative h-full ${plan.bgColor} border-4 border-black transform hover:rotate-0 transition-all duration-200 shadow-xl p-6 ${plan.rotateClass}`}>
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <div className="bg-[#79d45e] border-4 border-black px-4 py-2 transform rotate-6">
                          <div className="flex items-center font-black text-black text-sm">
                            <Zap className="mr-1 h-4 w-4" />
                            MOST POPULAR
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="text-center pb-8">
                      <div className="mx-auto mb-4 w-16 h-16 bg-white border-4 border-black rounded-full flex items-center justify-center">
                        <plan.icon className="h-8 w-8 text-black" />
                      </div>
                      <h3 className="text-2xl font-black text-black mb-2">{plan.name}</h3>
                      <p className="text-black font-semibold mb-4">
                        {plan.description}
                      </p>
                      <div className="mb-6">
                        <span className="text-4xl font-black text-black">{plan.price}</span>
                        {plan.period && (
                          <span className="text-black font-semibold ml-1">/{plan.period}</span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="bg-white border-4 border-black transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                        <Button 
                          className="w-full bg-[#4928fd] hover:bg-[#4928fd]/90 text-white border-0 font-black py-3"
                          size="lg"
                          asChild>
                          <Link href={plan.href}>
                            {plan.cta}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>

                      <div>
                        <h4 className="font-black text-black mb-3">What's included:</h4>
                        <ul className="space-y-2">
                          {plan.features.map((feature) => (
                            <li key={feature} className="flex items-start gap-2 text-sm">
                              <Check className="h-4 w-4 text-black mt-0.5 flex-shrink-0 font-bold" />
                              <span className="font-semibold text-black">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {plan.limitations.length > 0 && (
                        <div>
                          <h4 className="font-black text-black/70 mb-3">Not included:</h4>
                          <ul className="space-y-2">
                            {plan.limitations.map((limitation) => (
                              <li key={limitation} className="flex items-start gap-2 text-sm text-black/70">
                                <X className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <span className="font-semibold">{limitation}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Decorative corner elements */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-white border-2 border-black transform rotate-45"></div>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-black rounded-full"></div>
                  </div>
                </AnimatedGroup>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative px-6 py-16" style={{ backgroundColor: '#fdf1d3' }}>
          <div className="mx-auto max-w-4xl text-center">
            <AnimatedGroup variants={{ item: transitionVariants }}>
              <div className="bg-[#79d45e] border-4 border-black p-6 inline-block transform rotate-1 hover:rotate-0 transition-transform duration-200 mb-6">
                <h2 className="text-3xl md:text-4xl font-black text-black">
                  Ready to Get Started?
                </h2>
              </div>
              <div className="bg-white border-4 border-black p-4 inline-block transform -rotate-0.5 hover:rotate-0 transition-transform duration-200 mb-8">
                <p className="text-lg font-semibold text-black">
                  Join thousands of event organizers who trust SocialLoop for their networking events.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="bg-[#4928fd] border-4 border-black transform rotate-2 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-[#4928fd] hover:bg-[#4928fd]/90 text-white border-0 font-black" asChild>
                    <Link href="/signup">
                      Start Free Trial
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
                <div className="bg-white border-4 border-black transform -rotate-1 hover:rotate-0 transition-transform duration-200">
                  <Button size="lg" className="bg-white hover:bg-gray-50 text-black border-0 font-black" asChild>
                    <Link href="/contact">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Contact Sales
                    </Link>
                  </Button>
                </div>
              </div>
            </AnimatedGroup>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}